import React, { FC, ReactNode } from "react";
import OnboardingHeader from "./OnboardingHeader";
import { useLocale } from "./i18n/locale";

export interface OnboardingShellProps {
  /** 1-based index of the current step. */
  step: number;
  /** 4 for Flow A (short onboarding), 7 for Flow B (full onboarding). */
  totalSteps: number;
  title: string;
  subtitle?: string;
  /** Rendered under the card, e.g. the "100% secure & confidential" line. */
  hideTitle?: boolean;
  footnote?: ReactNode;
  /**
   * Steps 5 and 7 lay out in two columns once there is room for them. The
   * phone layout stays the base (spec: mobile-first, ~80% of tenants), so this
   * only widens the column from the sm breakpoint up.
   */
  wide?: boolean;
  children: ReactNode;
  className?: string;
}

const OnboardingShell: FC<OnboardingShellProps> = ({
  step,
  totalSteps,
  title,
  subtitle,
  hideTitle = false,
  footnote,
  wide = false,
  children,
  className = "",
}) => {
  const { locale, t } = useLocale();

  return (
    // lang lives on the onboarding root, not <html>: this is one subtree of a
    // site that is otherwise English-only, and mutating the document element
    // would outlive the step once the tenant navigates away.
    <div
      lang={locale}
      className={`nc-OnboardingShell min-h-screen bg-cream-50 ${className}`}
      data-nc-id="OnboardingShell"
    >
      <div
        className={`mx-auto flex min-h-screen w-full flex-col px-5 pb-10 pt-4 ${
          wide ? "max-w-md sm:max-w-3xl" : "max-w-md"
        }`}
      >
        <OnboardingHeader />

        <div className="mt-6 flex items-center gap-2">
          <span
            className="flex h-7 w-7 items-center justify-center rounded-full bg-primary-6000 text-sm font-semibold text-white"
            aria-hidden="true"
          >
            {step}
          </span>
          <span className="text-sm font-semibold text-primary-6000" aria-hidden="true">
            / {totalSteps}
          </span>
          <span className="sr-only">{t.common.stepOf(step, totalSteps)}</span>
        </div>

        <div className="flex flex-1 flex-col justify-center">
        {!hideTitle && (
          <>
        <h1 className="mt-6 sm:mt-3 text-center font-semibold font-serif text-[26px] leading-tight text-navy-900">
          {title}
        </h1>
        {subtitle && (
          <p className="mx-auto mt-2 max-w-none text-center text-[13px] leading-relaxed text-neutral-700 font-[600]">
            {subtitle}
          </p>
            )}
          </>
        )}

        <div className="mt-6">{children}</div>

        {footnote && (
          <div className="mt-4 flex items-center justify-center gap-1.5 text-xs text-neutral-700 font-semibold">
            {footnote}
          </div>
        )}
      </div>
      </div>
    </div>
  );
};

export default OnboardingShell;
